import React, { memo, useId, useState } from 'react';
import classNames from 'classnames';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import styles from './Accordion.module.scss';

interface Props {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  'data-test-id'?: string;
  defaultOpen?: boolean;
  title: React.ReactNode;
  toggleClassName?: string;
}

function Accordion(props: Props) {
  const {
    children,
    className,
    contentClassName,
    'data-test-id': dataTestId,
    defaultOpen = true,
    title,
    toggleClassName,
  } = props;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const panelId = useId();
  const labelId = useId();

  return (
    <div className={className}>
      <button
        id={labelId}
        type="button"
        className={classNames(
          styles.toggle,
          { [styles.toggleOpen]: isOpen },
          toggleClassName,
        )}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls={panelId}
        data-test-id={dataTestId}
      >
        <div className={styles.title}>
          {typeof title === 'string' ? (
            <Typography variant="body" strong>
              {title}
            </Typography>
          ) : (
            title
          )}
        </div>
        <MaterialIcon icon="arrow_drop_down" className={styles.icon} />
      </button>
      <div
        id={panelId}
        role="region"
        aria-labelledby={labelId}
        className={classNames(styles.panel, { [styles.panelOpen]: isOpen })}
        inert={!isOpen}
      >
        <div className={styles.panelInner}>
          <div className={classNames(styles.content, contentClassName)}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default memo(Accordion);
